import React, { useState } from "react";
import { DateTime } from "luxon";
import { TSailHomesReportRow, TCasesCaseForm, TAdhocTaskForms, TCasesCaseCreateAdhocForm } from "../../../../../../domain/codecs/form/CasesForm";
import { TCaseQuickSummaryWaitingOnOwner } from "../../../../../../domain/codecs/CaseQuickSummary";
import { TPlainUser } from "../../../../../../domain/codecs/User";
import { CRMColors } from "../../../../models/CRMColors";
import { THexColor } from "../../../../models/StringLiterals";
import { CRMSpacing } from "../../../../models/CRMSpacing";
import { CRMFontSizes } from "../../../../models/CRMFontSizes";
import { Background } from "../../../BuildingBlocks/Background";
import { Padding } from "../../../BuildingBlocks/Padding";
import { SpacingRow } from "../../../BuildingBlocks/SpacingRow";
import { SpacingColumn } from "../../../BuildingBlocks/SpacingColumn";
import { Text } from "../../../BuildingBlocks/Text";
import { CRMIcon } from "../../../CRMIcon/CRMIcon";
import { WeightBold } from "../../../WeightBold/WeightBold";
import { WeightSemiBold } from "../../../WeightSemiBold/WeightSemiBold";
import { CRMCaseTableRowTasks } from "../CRMCaseTableRowTasks/CRMCaseTableRowTasks";

type TCRMSailHomesReportRowProps = {
    row: TSailHomesReportRow;
    expandedCaseForm: TCasesCaseForm | undefined;
    assignableUsers: Array<TPlainUser>;
    onExpand: (caseId: string) => void;
    onGoToCase: (caseId: string) => void;
    onChangeTask: (form: TAdhocTaskForms) => void;
    onArchiveTask: (form: TAdhocTaskForms) => void;
    onSubmitNewTask: (form: TCasesCaseCreateAdhocForm) => void;
};

const ownerColor: Record<TCaseQuickSummaryWaitingOnOwner, THexColor> = {
    client: CRMColors.PRIMARY_4,
    other_side: CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_0,
    third_party: CRMColors.HIGHLIGHTS_CALMING_PINK_0,
    us: CRMColors.PRIMARY_0,
};

const ownerShortLabel: Record<TCaseQuickSummaryWaitingOnOwner, string> = {
    client: "Client",
    other_side: "Other side",
    third_party: "Third party",
    us: "Us",
};

const daysUnderOffer = (isoOrNull: string | null): number | null => {
    if (isoOrNull === null) {
        return null;
    }
    const accepted = DateTime.fromISO(isoOrNull.replace(" ", "T"), { zone: "utc" });
    if (!accepted.isValid) {
        return null;
    }
    return Math.max(0, Math.floor(DateTime.utc().diff(accepted, "days").days));
};

const daysToDeadline = (isoDate: string): number | null => {
    const deadline = DateTime.fromISO(isoDate, { zone: "Europe/London" }).startOf("day");
    if (!deadline.isValid) {
        return null;
    }
    const today = DateTime.utc().setZone("Europe/London").startOf("day");
    return Math.round(deadline.diff(today, "days").days);
};

const toDeadlineDayLabel = (isoDate: string): string => {
    const deadline = DateTime.fromISO(isoDate, { zone: "Europe/London" });
    return deadline.isValid ? deadline.toFormat("d MMM") : "";
};

const STAT_LANE_WIDTH = "140px";

type TDeadlineStat = {
    hero: string;
    caption: string;
    color: THexColor;
};

const deadlineStat = (exchangeDeadline: string | null, hasExchanged: boolean): TDeadlineStat | null => {
    if (exchangeDeadline === null) {
        return null;
    }
    const dayLabel = toDeadlineDayLabel(exchangeDeadline);
    if (dayLabel === "") {
        return null;
    }
    if (hasExchanged) {
        return {
            hero: "Exchanged",
            caption: `${dayLabel} deadline`,
            color: CRMColors.HIGHLIGHTS_POSITIVE_GREEN_0,
        };
    }
    const days = daysToDeadline(exchangeDeadline);
    if (days === null) {
        return null;
    }
    if (days < 0) {
        return {
            hero: String(Math.abs(days)),
            caption: Math.abs(days) === 1 ? "day over deadline" : "days over deadline",
            color: CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_0,
        };
    }
    if (days === 0) {
        return {
            hero: "Today",
            caption: "exchange deadline",
            color: CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_0,
        };
    }
    return {
        hero: String(days),
        caption: days === 1 ? "day to deadline" : "days to deadline",
        color: CRMColors.NEUTRAL_0,
    };
};

export const CRMSailHomesReportRow = (props: TCRMSailHomesReportRowProps): JSX.Element => {
    const [isExpanded, setIsExpanded] = useState(false);

    const days = daysUnderOffer(props.row.is_offer_accepted_at);
    const deadline = deadlineStat(props.row.exchange_deadline, props.row.has_exchanged);

    const toggleExpand = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (!isExpanded) {
            props.onExpand(props.row.case_id);
        }
        setIsExpanded(!isExpanded);
    };

    return (
        <Background
            borderDefinition={`1px solid ${CRMColors.NEUTRAL_10}`}
            borderRadius="8px"
            color={CRMColors.NEUTRAL_PAPER}
        >
            {/* COLLAPSED ROW — the click target. Deliberately not the outer card:
                that would make every control in the expanded task panel open a tab. */}
            <div
                className="crm-sail-homes-report-row__clickable"
                onClick={() => props.onGoToCase(props.row.case_id)}
            >
                <Padding spacing={CRMSpacing.MEDIUM}>
                    <SpacingRow childSize="1fr max-content" alignItems="flex-start" spacing={CRMSpacing.MEDIUM}>
                        <SpacingColumn spacing={CRMSpacing.SMALL}>
                            {/* Address + handler */}
                            <SpacingRow spacing={CRMSpacing.SMALL} alignItems="center">
                                <Text size={CRMFontSizes.MED} weight={700} color={CRMColors.NEUTRAL_INK}>
                                    {props.row.address || "(no address)"}
                                </Text>
                                {props.row.handler !== null &&
                                    <Text size={CRMFontSizes.SMALL} color={CRMColors.NEUTRAL_2}>
                                        {props.row.handler.first_name} {props.row.handler.last_name}
                                    </Text>
                                }
                            </SpacingRow>

                            {/* WHAT'S NEXT — the hero: 1-2 owner-tagged actions */}
                            {props.row.is_stale && props.row.whats_next.length === 0
                                ? <Text size={CRMFontSizes.SMALL} color={CRMColors.NEUTRAL_2}>Updating…</Text>
                                : props.row.whats_next.length === 0
                                    ? <Text size={CRMFontSizes.SMALL} color={CRMColors.NEUTRAL_2}>No next actions</Text>
                                    : <SpacingColumn spacing={CRMSpacing.TINY}>
                                        {props.row.whats_next.map((item, i) => (
                                            <SpacingRow key={i} spacing={CRMSpacing.TINY} alignItems="baseline">
                                                {item.owner !== null &&
                                                    <Text size={CRMFontSizes.SMALL} weight={700} color={ownerColor[item.owner]}>
                                                        {ownerShortLabel[item.owner]}
                                                    </Text>
                                                }
                                                <Text size={CRMFontSizes.SMALL} color={CRMColors.NEUTRAL_INK}>
                                                    <WeightSemiBold>{item.action}</WeightSemiBold>
                                                </Text>
                                            </SpacingRow>
                                        ))}
                                        {props.row.is_stale &&
                                            <Text size={CRMFontSizes.X_SMALL} color={CRMColors.NEUTRAL_2}>Updating…</Text>
                                        }
                                    </SpacingColumn>
                            }

                            {/* CHASE REASONS */}
                            {props.row.chase_reasons_readable.length > 0 &&
                                <Text size={CRMFontSizes.X_SMALL} color={CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_0}>
                                    Chase: {props.row.chase_reasons_readable.join(", ")}
                                </Text>
                            }
                        </SpacingColumn>

                        {/* RIGHT — exchange deadline + days under offer + expand chevron */}
                        <SpacingRow spacing={CRMSpacing.MEDIUM} alignItems="center">
                            <Background width={STAT_LANE_WIDTH}>
                                {deadline !== null &&
                                    <SpacingColumn spacing="0px">
                                        <Text size={CRMFontSizes.SMALL} weight={700} align="right" color={deadline.color}>
                                            {deadline.hero}
                                        </Text>
                                        <Text size={CRMFontSizes.SMALL} align="right" color={CRMColors.NEUTRAL_2}>
                                            {deadline.caption}
                                        </Text>
                                    </SpacingColumn>
                                }
                            </Background>
                            <Background width={STAT_LANE_WIDTH}>
                                {days !== null &&
                                    <SpacingColumn spacing="0px">
                                        <Text size={CRMFontSizes.SMALL} weight={700} align="right" color={days >= 60 ? CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_0 : CRMColors.NEUTRAL_0}>
                                            {days}
                                        </Text>
                                        <Text size={CRMFontSizes.SMALL} align="right" color={CRMColors.NEUTRAL_2}>
                                            {days === 1 ? "day under offer" : "days under offer"}
                                        </Text>
                                    </SpacingColumn>
                                }
                            </Background>
                            <Background cursor="pointer" onClick={toggleExpand}>
                                <CRMIcon
                                    iconName={isExpanded ? "up-arrow" : "down-arrow"}
                                    colour="neutral-ink"
                                    size="tiny"
                                />
                            </Background>
                        </SpacingRow>
                    </SpacingRow>
                </Padding>
            </div>

            {/* EXPANDED — interactive task panel (reused from the Case view) */}
            {isExpanded &&
                <Background color={CRMColors.NEUTRAL_12}>
                    <Padding spacing={CRMSpacing.MEDIUM}>
                        {props.expandedCaseForm === undefined
                            ? <Text size={CRMFontSizes.SMALL} color={CRMColors.NEUTRAL_2}>Loading tasks…</Text>
                            : <SpacingColumn spacing={CRMSpacing.SMALL}>
                                <Background cursor="pointer" onClick={() => props.onGoToCase(props.row.case_id)}>
                                    <Text size={CRMFontSizes.SMALL} color={CRMColors.PRIMARY_0}>
                                        <WeightBold>Open case ›</WeightBold>
                                    </Text>
                                </Background>
                                <CRMCaseTableRowTasks
                                    form={props.expandedCaseForm}
                                    assignableUsers={props.assignableUsers}
                                    onChangeTask={props.onChangeTask}
                                    onArchiveTask={props.onArchiveTask}
                                    onSubmitNewTask={props.onSubmitNewTask}
                                />
                            </SpacingColumn>
                        }
                    </Padding>
                </Background>
            }
        </Background>
    );
};
